import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { ProductPageContext } from './Calendar';

import { useTranslation } from 'react-i18next';
// import useElDimensions from '../hooks/useElDimensions';
import usePropsBeforeContext from '../hooks/usePropsBeforeContext';

import ChooseProductMasonry from './base/ChooseProductMasonry';
import ChooseProductCard from './base/ChooseProductCard';
export default function ProductSelector(props) {
  // const [headlineRef, headlineDimensions] = useElDimensions();
  const { t, i18n } = useTranslation();

  const {
    onProductSelected,
    products = [],
    selectProduct,
    unsupportedProductNotice = false,
  } = usePropsBeforeContext(props, ProductPageContext);

  const language = i18n?.resolvedLanguage || i18n?.language;

  // Copy before sorting: the same array is handed around via ProductPageContext
  // and Calendar reads products[0] as its resource-switch fallback.
  const sortedProducts = useMemo(() => {
    const collator = new Intl.Collator(language || undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    return [...products].sort((a, b) =>
      collator.compare(a?.name ?? '', b?.name ?? ''),
    );
  }, [products, language]);

  // Skipped while the notice is up: the dialog was opened precisely so the
  // customer re-picks, so auto-selecting would close it again immediately.
  if (!unsupportedProductNotice && products?.length === 1) {
    selectProduct(products[0]);
    if (onProductSelected) {
      onProductSelected(products[0]);
    }
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Grid item xs={12}>
        <Typography variant="h4" component={'h2'}>
          {t('product_selection_headline')}
        </Typography>
      </Grid>
      {unsupportedProductNotice && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('product_unavailable_for_resource_warning', "Chosen resource doesn't support the product. Choose another one.")}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ maxHeight: '100%', overflow: 'auto' }}>
        <ChooseProductMasonry products={sortedProducts} isLoading={false}>
          {sortedProducts.map((product, index) => (
            <ChooseProductCard
              index={index}
              key={product.uuid}
              title={product.name}
              description={product.detailsPre}
              minDuration={product.minDuration}
              titleTypographyProps={{
                variant: 'body1',
              }}
              onSelectProduct={() => {
                selectProduct(product);
                if (onProductSelected) {
                  onProductSelected();
                }
              }}
            />
          ))}
        </ChooseProductMasonry>
      </Grid>
    </Grid>
  );
}

ProductSelector.propTypes = {
  /** callback for parent to trigger action on produc selection */
  onProductSelected: PropTypes.func,
  /** renders a hint explaining the newly selected resource doesn't offer the previously chosen product */
  unsupportedProductNotice: PropTypes.bool,
};
